import { DataModelApiRequest, DataModelApiResponse, FinalizeModelResponse } from '../types/dataModel';

interface StreamingCallbacks {
  onUpdate?: (update: DataModelApiResponse) => void;
  onComplete?: (finalUpdate: DataModelApiResponse | null) => void;
  onError?: (error: Error) => void;
  onGeneratingSamples?: () => void;  // Called when stream completes and sample generation starts
}

export class DataModelApiService {
  private static readonly BASE_URL = '/api/projects';

  static async streamChat(
    request: DataModelApiRequest,
    callbacks: StreamingCallbacks,
    projectId: string
  ): Promise<void> {
    const { onUpdate, onComplete, onError, onGeneratingSamples } = callbacks;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${this.BASE_URL}/${projectId}/datamodel-agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastUpdate: DataModelApiResponse | null = null;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process newline-delimited JSON
          const lines = buffer.split('\n');
          // Keep the last item in the buffer because it doesn't end with a newline
          buffer = lines.pop() || '';

          // Process each complete line
          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const update: DataModelApiResponse = JSON.parse(line);

              // Handle heartbeat messages
              if (update.type === 'HEARTBEAT' && update.status === 'generating_samples') {
                console.log('Received heartbeat:', update.message);
                if (typeof onGeneratingSamples === 'function') {
                  onGeneratingSamples();
                }
                continue;
              }

              lastUpdate = update;

              if (typeof onUpdate === 'function' && !update.is_complete) {
                onUpdate(update);
              }
            } catch (parseError) {
              console.error('Failed to parse line as JSON:', line, parseError);
            }
          }
        }

        reader.cancel();

        if (typeof onComplete === 'function') {
          onComplete(lastUpdate);
        }
      } catch (streamError) {
        console.error('Stream read error:', streamError);
        if (typeof onError === 'function') {
          onError(streamError as Error);
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (typeof onError === 'function') {
        onError(error as Error);
      }
    }
  }

  static async sendInitialMessage(
    systemDescription: string,
    callbacks: StreamingCallbacks,
    projectId: string
  ): Promise<void> {
    const request: DataModelApiRequest = {
      system_description: systemDescription,
    };

    return this.streamChat(request, callbacks, projectId);
  }

  static async sendFollowUpMessage(
    query: string,
    systemDescription: string,
    callbacks: StreamingCallbacks,
    projectId: string,
    currentDataModel?: any,
    context?: Record<string, any>
  ): Promise<void> {
    const request: DataModelApiRequest = {
      query,
      system_description: systemDescription,
      // Only send context if frontend is managing it (as a dictionary/object)
      ...(context && typeof context === 'object' && !Array.isArray(context) ? { context } : {})
    };

    return this.streamChat(request, callbacks, projectId);
  }

  static async finalizeModel(
    projectId: string,
    callbacks: {
      onAction?: (message: string) => void;
      onComplete?: (success: boolean, message: string) => void;
      onError?: (error: Error) => void;
    }
  ): Promise<void> {
    const { onAction, onComplete, onError } = callbacks;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      console.log('Finalizing model for project:', projectId);

      const response = await fetch(`${this.BASE_URL}/${projectId}/datamodel-agent/createModel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let hasError = false;
      let finalMessage = '';

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process newline-delimited JSON
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const update = JSON.parse(line);
              
              // Handle ACTION type messages
              if (update.type === 'ACTION') {
                const message = update.message || '';
                console.log('Action update:', message);
                
                // Check for error messages
                if (message.toLowerCase().includes('error')) {
                  hasError = true;
                }
                
                // Send action update to callback
                if (typeof onAction === 'function') {
                  onAction(message);
                }
                
                // Store final message if complete
                if (update.is_complete) {
                  finalMessage = message;
                }
              }
            } catch (parseError) {
              console.error('Failed to parse line as JSON:', line, parseError);
            }
          }
        }

        reader.cancel();

        // Determine success based on whether errors were encountered
        const success = !hasError;
        const message = finalMessage || (success ? 'Data model finalized successfully!' : 'Some errors occurred during finalization');

        if (typeof onComplete === 'function') {
          onComplete(success, message);
        }
      } catch (streamError) {
        console.error('Stream read error:', streamError);
        if (typeof onError === 'function') {
          onError(streamError as Error);
        }
      }
    } catch (error) {
      console.error('Error finalizing model:', error);
      if (typeof onError === 'function') {
        onError(error as Error);
      }
    }
  }

  static async getDataModelPreview(projectId: string): Promise<{ data_model: any | null; exists: boolean }> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${this.BASE_URL}/${projectId}/datamodel-agent/preview`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching data model preview:', error);
      return {
        data_model: null,
        exists: false
      };
    }
  }

  static async saveDataModel(projectId: string, dataModel: any): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      console.log('Saving data model to backend for project:', projectId);

      const response = await fetch(`${this.BASE_URL}/${projectId}/datamodel-agent/save`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ data_model: dataModel }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Data model saved successfully:', result);

      return {
        success: true,
        message: result.message || 'Data model saved successfully'
      };
    } catch (error) {
      console.error('Error saving data model:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save data model'
      };
    }
  }
}